import { useState, useEffect } from 'react';
import { Button, Form, Input, Modal, Select, Tag, Table, message, InputNumber } from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined, SearchOutlined, DownloadOutlined } from '@ant-design/icons';
import { useWeddingPlannerStore } from '../../stores/weddingPlannerStore';

const STATUS_COLORS = { coming: 'green', not_coming: 'red', pending: 'gold' };
const STATUS_LABELS = { coming: 'Keladi', not_coming: 'Kelmaydi', pending: 'Javob bermadi' };
const SIDE_LABELS = { groom: 'Kuyov', bride: 'Kelin', both: 'Umumiy' };
const SIDE_COLORS = { groom: 'blue', bride: 'pink', both: 'default' };

const WeddingGuestsPage = () => {
  const {
    guests, guestGroups, guestStats,
    fetchGuests, addGuest, updateGuest, deleteGuest, addGuestsBulk,
  } = useWeddingPlannerStore();

  const [modalOpen, setModalOpen] = useState(false);
  const [editingGuest, setEditingGuest] = useState(null);
  const [form] = Form.useForm();
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState(null);
  const [filterSide, setFilterSide] = useState(null);
  const [filterGroup, setFilterGroup] = useState(null);

  useEffect(() => {
    fetchGuests();
  }, []);

  const handleSearch = () => {
    fetchGuests({ search, status: filterStatus, side: filterSide, group_name: filterGroup });
  };

  useEffect(() => {
    const timer = setTimeout(() => handleSearch(), 300);
    return () => clearTimeout(timer);
  }, [search, filterStatus, filterSide, filterGroup]);

  const openAdd = () => {
    setEditingGuest(null);
    form.resetFields();
    form.setFieldsValue({ status: 'pending', side: 'both', people_count: 1 });
    setModalOpen(true);
  };

  const openEdit = (guest) => {
    setEditingGuest(guest);
    form.setFieldsValue(guest);
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (editingGuest) {
        await updateGuest(editingGuest.id, values);
        message.success('Mehmon yangilandi');
      } else {
        await addGuest(values);
        message.success("Mehmon qo'shildi");
      }
      setModalOpen(false);
      form.resetFields();
      fetchGuests({ search, status: filterStatus, side: filterSide, group_name: filterGroup });
    } catch (e) {}
  };

  const handleDelete = async (id) => {
    await deleteGuest(id);
    message.success("Mehmon o'chirildi");
    fetchGuests({ search, status: filterStatus, side: filterSide, group_name: filterGroup });
  };

  const handleExportCSV = () => {
    if (!guests.length) return;
    const headers = ['Ism', 'Telefon', 'Status', 'Tomon', 'Guruh', 'Stol', 'Kishi soni', 'Izoh'];
    const rows = guests.map(g => [
      g.name, g.phone || '', STATUS_LABELS[g.status] || '',
      SIDE_LABELS[g.side] || '', g.group_name || '',
      g.table_number || '', g.people_count || 1, g.notes || '',
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.map(v => `"${v}"`).join(','))].join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'mehmonlar.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  const stats = guestStats;

  const columns = [
    {
      title: 'Ism',
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => (
        <div>
          <span style={{ fontWeight: 600 }}>{name}</span>
          {record.group_name && (
            <Tag style={{ marginLeft: 6, fontSize: 10, borderRadius: 6 }}>{record.group_name}</Tag>
          )}
        </div>
      ),
    },
    {
      title: 'Telefon',
      dataIndex: 'phone',
      key: 'phone',
      render: (v) => v || '—',
      responsive: ['md'],
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={STATUS_COLORS[status] || 'default'}>
          {STATUS_LABELS[status] || 'Javob bermadi'}
        </Tag>
      ),
    },
    {
      title: 'Tomon',
      dataIndex: 'side',
      key: 'side',
      render: (side) => (
        <Tag color={SIDE_COLORS[side] || 'default'}>
          {SIDE_LABELS[side] || 'Umumiy'}
        </Tag>
      ),
      responsive: ['md'],
    },
    {
      title: 'Stol',
      dataIndex: 'table_number',
      key: 'table_number',
      render: (v) => v ? `Stol ${v}` : '—',
      responsive: ['lg'],
    },
    {
      title: 'Kishi',
      dataIndex: 'people_count',
      key: 'people_count',
      render: (v) => v || 1,
    },
    {
      title: '',
      key: 'actions',
      render: (_, record) => (
        <div style={{ display: 'flex', gap: 4 }}>
          <Button size="small" type="text" icon={<EditOutlined />} onClick={() => openEdit(record)} />
          <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record.id)} />
        </div>
      ),
    },
  ];

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', width: '100%', padding: '16px 16px 40px' }}>
      {/* Header + stats */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 18 }}>Mehmonlar ro'yxati</div>
          <div style={{ fontSize: 13, color: '#888' }}>
            {stats.total || 0} ta mehmon &bull; {stats.total_people || 0} kishi
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button icon={<DownloadOutlined />} onClick={handleExportCSV}>CSV</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}
            style={{ background: 'linear-gradient(135deg, #5F33E1, #8B5CF6)', border: 'none', borderRadius: 10 }}>
            Qo'shish
          </Button>
        </div>
      </div>

      {/* Stat cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10, marginBottom: 16 }}>
        <div style={{ padding: 12, borderRadius: 12, background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.2)', textAlign: 'center' }}>
          <div style={{ fontWeight: 700, fontSize: 20, color: '#10B981' }}>{stats.coming || 0}</div>
          <div style={{ fontSize: 12, color: '#666' }}>Keladi</div>
        </div>
        <div style={{ padding: 12, borderRadius: 12, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', textAlign: 'center' }}>
          <div style={{ fontWeight: 700, fontSize: 20, color: '#EF4444' }}>{stats.not_coming || 0}</div>
          <div style={{ fontSize: 12, color: '#666' }}>Kelmaydi</div>
        </div>
        <div style={{ padding: 12, borderRadius: 12, background: 'rgba(212,175,55,0.08)', border: '1px solid rgba(212,175,55,0.2)', textAlign: 'center' }}>
          <div style={{ fontWeight: 700, fontSize: 20, color: '#D4AF37' }}>{stats.pending || 0}</div>
          <div style={{ fontSize: 12, color: '#666' }}>Javob bermadi</div>
        </div>
        <div style={{ padding: 12, borderRadius: 12, background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.2)', textAlign: 'center' }}>
          <div style={{ fontWeight: 700, fontSize: 20, color: '#8B5CF6' }}>{stats.coming_people || 0}</div>
          <div style={{ fontSize: 12, color: '#666' }}>Keladi (kishi)</div>
        </div>
        {stats.by_side && (
          <>
            <div style={{ padding: 12, borderRadius: 12, background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.2)', textAlign: 'center' }}>
              <div style={{ fontWeight: 700, fontSize: 20, color: '#3B82F6' }}>{stats.by_side.groom || 0}</div>
              <div style={{ fontSize: 12, color: '#666' }}>Kuyov tomoni</div>
            </div>
            <div style={{ padding: 12, borderRadius: 12, background: 'rgba(236,72,153,0.08)', border: '1px solid rgba(236,72,153,0.2)', textAlign: 'center' }}>
              <div style={{ fontWeight: 700, fontSize: 20, color: '#EC4899' }}>{stats.by_side.bride || 0}</div>
              <div style={{ fontSize: 12, color: '#666' }}>Kelin tomoni</div>
            </div>
          </>
        )}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <Input
          prefix={<SearchOutlined />}
          placeholder="Qidirish..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ flex: 1, minWidth: 150, borderRadius: 10 }}
          allowClear
        />
        <Select
          value={filterStatus}
          onChange={v => setFilterStatus(v)}
          placeholder="Status"
          allowClear
          style={{ minWidth: 130 }}
          options={[
            { value: 'coming', label: 'Keladi' },
            { value: 'not_coming', label: 'Kelmaydi' },
            { value: 'pending', label: 'Javob bermadi' },
          ]}
        />
        <Select
          value={filterSide}
          onChange={v => setFilterSide(v)}
          placeholder="Tomon"
          allowClear
          style={{ minWidth: 120 }}
          options={[
            { value: 'groom', label: 'Kuyov' },
            { value: 'bride', label: 'Kelin' },
            { value: 'both', label: 'Umumiy' },
          ]}
        />
        {guestGroups.length > 0 && (
          <Select
            value={filterGroup}
            onChange={v => setFilterGroup(v)}
            placeholder="Guruh"
            allowClear
            style={{ minWidth: 130 }}
            options={guestGroups.map(g => ({ value: g, label: g }))}
          />
        )}
      </div>

      {/* Table */}
      <Table
        dataSource={guests}
        columns={columns}
        rowKey="id"
        size="small"
        pagination={{ pageSize: 20, showSizeChanger: true, pageSizeOptions: [10, 20, 50, 100] }}
        scroll={{ x: 520 }}
      />

      {/* Add/Edit Modal */}
      <Modal
        title={editingGuest ? 'Mehmonni tahrirlash' : "Yangi mehmon qo'shish"}
        open={modalOpen}
        onOk={handleSubmit}
        onCancel={() => { setModalOpen(false); form.resetFields(); }}
        okText={editingGuest ? 'Saqlash' : "Qo'shish"}
        cancelText="Bekor"
        width={520}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Ism" rules={[{ required: true, message: 'Ismni kiriting' }]}>
            <Input placeholder="Ali Valiyev" />
          </Form.Item>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Form.Item name="phone" label="Telefon">
              <Input placeholder="+998 90 123 45 67" />
            </Form.Item>
            <Form.Item name="side" label="Tomon" initialValue="both">
              <Select>
                <Select.Option value="groom">Kuyov tomoni</Select.Option>
                <Select.Option value="bride">Kelin tomoni</Select.Option>
                <Select.Option value="both">Umumiy</Select.Option>
              </Select>
            </Form.Item>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Form.Item name="status" label="Status" initialValue="pending">
              <Select>
                <Select.Option value="coming">Keladi</Select.Option>
                <Select.Option value="not_coming">Kelmaydi</Select.Option>
                <Select.Option value="pending">Javob bermadi</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item name="group_name" label="Guruh">
              <Input placeholder="Masalan: Oila, Do'stlar" />
            </Form.Item>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Form.Item name="table_number" label="Stol raqami">
              <InputNumber placeholder="5" min={1} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="people_count" label="Necha kishi" initialValue={1}>
              <InputNumber placeholder="2" min={1} style={{ width: '100%' }} />
            </Form.Item>
          </div>
          <Form.Item name="notes" label="Izoh">
            <Input.TextArea rows={2} placeholder="Qo'shimcha ma'lumot..." maxLength={500} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default WeddingGuestsPage;
